/**
 * Customer capability helpers derived from Keycloak realm roles.
 *
 * The customer realm defines a read-only `stratforth_viewer` role alongside
 * editor roles. We hide edit-oriented affordances from users who are
 * positively identified as viewer-only, so they stop hitting 403s. We never
 * hide affordances when the role set is empty/unknown (dev, dev-no-auth, or
 * tokens without realm roles) to avoid regressing those environments.
 */

export const VIEWER_ROLE = 'stratforth_viewer';

// Realm roles the backend services map to ADMIN, TENANT_ADMIN, DATA_ENGINEER or DATA_ANALYST
// (each service's SecurityConfig role map).
export const EDITOR_ROLES = [
  'stratforth_admin',
  'tenant_admin',
  'stratforth_tenant_admin',
  'stratforth_customer_admin',
  'stratforth_product_admin',
  'stratforth_data_engineer',
  'stratforth_data_analyst',
];

/** True only when we can positively tell the user is a read-only viewer. */
export const isViewerOnly = (roles?: string[] | null): boolean => {
  if (!roles || roles.length === 0) {
    return false;
  }
  const hasViewer = roles.includes(VIEWER_ROLE);
  const hasEditor = roles.some(role => EDITOR_ROLES.includes(role));
  return hasViewer && !hasEditor;
};

/** True when the user may use edit affordances (default-open when unknown). */
export const canEdit = (roles?: string[] | null): boolean => !isViewerOnly(roles);

// Realm roles analytics maps to ANALYTICS_USER, ANALYTICS_ADMIN or ADMIN, the roles its transformation
// job API requires (analytics SecurityConfig role map; TransformationJobController). Analytics lowercases
// a role before mapping it, so membership is checked on the lowercased role.
const TRANSFORMATION_ROLES = new Set([
  'stratforth_admin',
  'stratforth_product_admin',
  'analytics_user',
  'analytics_admin',
  'admin',
]);

/**
 * Gate for the Transformation Workbench. Default-open when roles are unknown, like {@link canEdit};
 * otherwise it needs a role the transformation job API admits, so editors without one (for example
 * `tenant_admin` or `stratforth_data_engineer` alone) are not offered a page that 403s.
 */
export const canUseTransformations = (roles?: string[] | null): boolean =>
  !roles || roles.length === 0 || roles.some(role => TRANSFORMATION_ROLES.has(role.toLowerCase()));

// Realm roles the action service maps to ADMIN or TENANT_ADMIN (action SecurityConfig role map).
const ACTION_ADMIN_ROLES = new Set([
  'stratforth_admin',
  'tenant_admin',
  'stratforth_tenant_admin',
  'stratforth_customer_admin',
  'ROLE_ADMIN',
  'ROLE_TENANT_ADMIN',
]);

/** Fail-closed gate for action-group administration; unknown roles never gain directory access. */
export const canAdminActions = (roles?: string[] | null): boolean =>
  Boolean(roles?.some(role => ACTION_ADMIN_ROLES.has(role)));

/**
 * Fail-closed gate for rule OPERATIONS (arm/disarm/kill/untrip/replay), ADR-022 §12. Kept as a
 * distinct helper from {@link canAdminActions} so rule authorization can diverge from action-group
 * administration without a wide refactor. Uses the same tenant-admin role set today.
 */
export const canAdminRules = (roles?: string[] | null): boolean =>
  Boolean(roles?.some(role => ACTION_ADMIN_ROLES.has(role)));

/**
 * Fail-closed gate for OUTBOUND CHANNEL administration (ADR-023 §3). The channels config API is
 * tenant-admin gated on the backend; this hides the customer channels surface from non-admins so they stop
 * hitting 403s. Kept as a distinct helper (same tenant-admin role set today) so channel authorization can
 * diverge from rule/action-group administration without a wide refactor.
 */
export const canAdminChannels = (roles?: string[] | null): boolean =>
  Boolean(roles?.some(role => ACTION_ADMIN_ROLES.has(role)));
